import React from "react";
import { useNavigate } from "react-router-dom";
import { ListChecks } from "lucide-react";
import { Card, ModernCardHeader } from "../../../components/cards";
import { Loading } from "../../../components/loading";
import { EmptyList } from "../../../components/empty-list";
import { useCustomerTasks, useSetCustomerTaskStatus } from "../hooks";
import { useMe } from "../../users/hooks/useMe";
import { CustomerTaskStatus } from "../../../__generated__/globalTypes";
import { customerTaskPriorityInfo } from "../../../helpers/enum-labels";
import { formatDisplayDate } from "../../../helpers/date";
import { dueDateStatus, DUE_DATE_STYLES } from "../due-date";

export const MyTasksWidget: React.FC = () => {
  const navigate = useNavigate();
  const { data: meData } = useMe();
  const meId = meData?.me?.id;
  const { data, loading } = useCustomerTasks({
    where: { assignedToId: meId ?? undefined },
  });
  const { setStatus } = useSetCustomerTaskStatus();

  const tasks = (data?.customerTasks?.results ?? []).filter(
    (task) => task.status !== CustomerTaskStatus.Done
  );

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    e.stopPropagation();
    setStatus(id, e.target.checked ? CustomerTaskStatus.Done : CustomerTaskStatus.Todo);
  };

  return (
    <Card>
      <ModernCardHeader
        title="Mes tâches"
        subtitle="Tâches qui vous sont assignées"
        action={
          <span
            className="text-xs text-indigo-600 cursor-pointer hover:underline"
            onClick={() => navigate("/customer-tasks")}
          >
            Voir tout
          </span>
        }
      />
      {loading ? (
        <Loading />
      ) : !tasks.length ? (
        <EmptyList text="Aucune tâche en cours" />
      ) : (
        <div className="space-y-1 mt-3">
          {tasks.slice(0, 5).map((task) => {
            const priority = customerTaskPriorityInfo(task.priority);
            // La liste exclut déjà les tâches Done, l'échéance est donc pertinente.
            const due = dueDateStatus(task.dueDate);
            return (
              <div
                key={`my-task-${task.id}`}
                className="flex items-center justify-between gap-2 p-2 rounded-md hover:bg-gray-50 cursor-pointer border border-transparent hover:border-gray-200"
                onClick={() =>
                  navigate(
                    task.customerId
                      ? `/customer/${task.customerId}`
                      : `/customer-task/${task.id}/update`
                  )
                }
              >
                <div className="flex items-center gap-2 min-w-0">
                  <input
                    type="checkbox"
                    checked={task.status === CustomerTaskStatus.Done}
                    onChange={(e) => handleToggle(e, task.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0"
                  />
                  <div className="bg-indigo-50 text-indigo-600 p-1.5 rounded-md flex-shrink-0">
                    <ListChecks className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-gray-900 truncate">{task.title}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {task.customer?.name ?? "Tâche interne"}
                    </p>
                    {task.dueDate && due !== "none" && (
                      <span
                        className={`inline-block mt-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${DUE_DATE_STYLES[due]}`}
                      >
                        {formatDisplayDate(task.dueDate)}
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${priority.color}`}
                >
                  {priority.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
